/**
 * iter-385 — Dashboard Record-Transaction Quick Action.
 *
 * Modal that lets an owner/staff log a payment against a project in one
 * shot. Two toggles at the top pick the direction:
 *   • Client Received  →  POST /api/payments/record
 *   • Vendor Paid      →  POST /api/projects/{id}/record-vendor-payment
 *
 * Fields:
 *   - Project (searchable dropdown)
 *   - Payment Stage — dynamically loaded from the selected project's
 *     `payment_schedule` (milestones). Optional, saved as notes prefix.
 *   - Amount (₹), Payment Mode, Reference, Notes
 *
 * Every transaction carries `project_id` so the project's finance
 * ledger picks it up automatically.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { IndianRupee, ArrowDownCircle, ArrowUpCircle } from 'lucide-react';
import { API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import SearchableCombobox from '@/components/SearchableCombobox';

const PAYMENT_MODES = ['Bank Transfer', 'Cash', 'Cheque', 'UPI', 'Card', 'Wallet'];

export default function RecordTransactionDialog({ open, onOpenChange, onRecorded }) {
  const [direction, setDirection] = useState('client_received'); // client_received | vendor_paid
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [vendors, setVendors] = useState([]);
  const [vendorId, setVendorId] = useState('');
  const [stage, setStage] = useState('');
  const [amount, setAmount] = useState('');
  const [paymentMode, setPaymentMode] = useState('Bank Transfer');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Fetch projects on open.
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/projects`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        setProjects(r.data || []);
      } catch { /* silent */ }
      // Vendors for the Vendor Paid mode.
      try {
        const v = await axios.get(`${API}/users`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        setVendors((v.data || []).filter((u) => u.role === 'vendor'));
      } catch { /* silent */ }
    })();
  }, [open]);

  const selectedProject = useMemo(
    () => projects.find((p) => p.id === projectId) || null,
    [projects, projectId],
  );

  // Payment schedule for the selected project — used to build the stage dropdown.
  const paymentStages = useMemo(() => {
    const sched = selectedProject?.payment_schedule || [];
    return sched.map((s, i) => ({
      value: s.milestone_name || `Stage ${i + 1}`,
      label: `${s.milestone_name || `Stage ${i + 1}`}${s.percentage != null ? ` — ${s.percentage}%` : ''}`,
    }));
  }, [selectedProject]);

  const reset = () => {
    setProjectId('');
    setVendorId('');
    setStage('');
    setAmount('');
    setReference('');
    setNotes('');
    setPaymentMode('Bank Transfer');
  };

  const submit = async () => {
    if (!projectId) { toast.error('Please pick a project'); return; }
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    if (direction === 'vendor_paid' && !vendorId) {
      toast.error('Pick the vendor being paid'); return;
    }
    const stagePrefix = stage ? `[${stage}] ` : '';
    setSaving(true);
    try {
      if (direction === 'client_received') {
        await axios.post(`${API}/payments/record`, {
          project_id: projectId,
          amount: amt,
          payment_mode: paymentMode,
          payment_reference: reference,
          notes: `${stagePrefix}${notes}`.trim(),
        }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      } else {
        const vendor = vendors.find((v) => v.id === vendorId);
        await axios.post(`${API}/projects/${projectId}/record-vendor-payment`, {
          vendor_id: vendorId,
          vendor_name: vendor?.name || '',
          amount: amt,
          payment_mode: paymentMode,
          payment_date: new Date().toISOString(),
          notes: `${stagePrefix}${notes}`.trim(),
        }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      }
      toast.success(direction === 'client_received' ? 'Client payment recorded' : 'Vendor payment recorded');
      onRecorded?.();
      onOpenChange?.(false);
      reset();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to record transaction');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange?.(v); if (!v) reset(); }}>
      <DialogContent className="max-w-lg" data-testid="record-transaction-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg flex items-center gap-2">
            <IndianRupee size={18} /> Record Transaction
          </DialogTitle>
          <p className="text-xs text-stone-500">
            Log a payment against a project. Every entry is saved with a project reference and shows up in that project&apos;s finance ledger automatically.
          </p>
        </DialogHeader>

        <div className="space-y-3">
          {/* Direction toggle */}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setDirection('client_received')}
              data-testid="txn-direction-client"
              className={`p-3 rounded-lg border-2 flex flex-col items-center gap-1 transition-all ${
                direction === 'client_received'
                  ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                  : 'border-stone-200 hover:border-stone-300'
              }`}
            >
              <ArrowDownCircle size={20} />
              <span className="text-sm font-medium">Client Received</span>
              <span className="text-[10px] text-stone-500">Money in — client paid</span>
            </button>
            <button
              type="button"
              onClick={() => setDirection('vendor_paid')}
              data-testid="txn-direction-vendor"
              className={`p-3 rounded-lg border-2 flex flex-col items-center gap-1 transition-all ${
                direction === 'vendor_paid'
                  ? 'border-red-500 bg-red-50 text-red-700'
                  : 'border-stone-200 hover:border-stone-300'
              }`}
            >
              <ArrowUpCircle size={20} />
              <span className="text-sm font-medium">Vendor Paid</span>
              <span className="text-[10px] text-stone-500">Money out — you paid vendor</span>
            </button>
          </div>

          <div>
            <Label>Select Project *</Label>
            <SearchableCombobox
              options={projects.filter((p) => p.is_active !== false).map((p) => ({ value: p.id, label: p.name }))}
              value={projectId}
              onValueChange={setProjectId}
              placeholder="Search or pick a project…"
              data-testid="txn-project-picker"
            />
          </div>

          <div>
            <Label>Select Payment Stage</Label>
            <select
              value={stage}
              onChange={(e) => setStage(e.target.value)}
              disabled={!projectId || paymentStages.length === 0}
              className="w-full h-9 border border-stone-300 rounded px-2 text-sm bg-white disabled:opacity-50"
              data-testid="txn-stage-select"
            >
              <option value="">
                {!projectId
                  ? 'Pick a project first…'
                  : paymentStages.length === 0
                    ? 'No stages on this project — leave blank'
                    : '— Optional —'}
              </option>
              {paymentStages.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          {direction === 'vendor_paid' && (
            <div>
              <Label>Vendor *</Label>
              <SearchableCombobox
                options={vendors.map((v) => ({ value: v.id, label: v.name || v.email }))}
                value={vendorId}
                onValueChange={setVendorId}
                placeholder="Search vendor…"
                data-testid="txn-vendor-picker"
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Amount (₹) *</Label>
              <Input
                type="number"
                min="0"
                step="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="100000"
                data-testid="txn-amount-input"
              />
            </div>
            <div>
              <Label>Payment Mode</Label>
              <select
                value={paymentMode}
                onChange={(e) => setPaymentMode(e.target.value)}
                className="w-full h-9 border border-stone-300 rounded px-2 text-sm bg-white"
                data-testid="txn-mode-select"
              >
                {PAYMENT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>

          <div>
            <Label>Reference / UTR / Cheque #</Label>
            <Input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Optional"
              data-testid="txn-reference-input"
            />
          </div>

          <div>
            <Label>Notes</Label>
            <Textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional context"
              data-testid="txn-notes-input"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => onOpenChange?.(false)} disabled={saving}>Cancel</Button>
            <Button
              onClick={submit}
              disabled={saving || !projectId || !amount}
              className={direction === 'client_received' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}
              data-testid="txn-save-btn"
            >
              {saving ? 'Saving…' : direction === 'client_received' ? 'Record Client Payment' : 'Record Vendor Payment'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
